import { LOCALE_STORAGE_KEYS } from 'config/localStorageKeys';
import { useCallback, useEffect, useRef, useState } from 'react';

const MIN_ZOOM = -4;
const MAX_ZOOM = 6;
const FONT_SIZE_THRESHOLD = 21;
const DATA_ATTR = 'data-original-fs';

const getStoredZoom = (): number => {
  const stored = localStorage.getItem(LOCALE_STORAGE_KEYS.ZOOM_LEVEL);
  if (stored !== null) {
    const parsed = parseInt(stored, 10);
    if (!isNaN(parsed) && parsed >= MIN_ZOOM && parsed <= MAX_ZOOM) {
      return parsed;
    }
  }
  return 0;
};

const isIconElement = (el: HTMLElement): boolean =>
  el.classList.contains('anticon') || !!el.closest('.anticon');

const applyZoomToDOM = (offset: number) => {
  // Walk all elements, capture original font-size on first pass, then apply offset
  const elements = document.querySelectorAll('*');
  elements.forEach((el) => {
    const htmlEl = el as HTMLElement;
    // Skip script/style/meta elements and icon elements
    if (
      htmlEl.tagName === 'SCRIPT' ||
      htmlEl.tagName === 'STYLE' ||
      htmlEl.tagName === 'META' ||
      htmlEl.tagName === 'LINK' ||
      isIconElement(htmlEl)
    )
      return;

    let originalSize: number;
    const stored = htmlEl.getAttribute(DATA_ATTR);

    if (stored) {
      originalSize = parseFloat(stored);
    } else {
      const computed = window.getComputedStyle(htmlEl).fontSize;
      originalSize = parseFloat(computed);
      if (isNaN(originalSize)) return;
      // Only track elements with font-size < threshold
      if (originalSize >= FONT_SIZE_THRESHOLD) return;
      htmlEl.setAttribute(DATA_ATTR, String(originalSize));
    }

    if (offset === 0) {
      // Reset: remove inline font-size override and data attribute
      htmlEl.style.removeProperty('font-size');
      htmlEl.removeAttribute(DATA_ATTR);
    } else {
      const newSize = originalSize + offset;
      // Don't let fonts go below 8px
      htmlEl.style.fontSize = `${Math.max(newSize, 8)}px`;
    }
  });
};

export const useZoom = () => {
  const [zoomLevel, setZoomLevel] = useState(getStoredZoom);
  const [isZooming, setIsZooming] = useState(false);
  const observerRef = useRef<MutationObserver | null>(null);
  const zoomRef = useRef(getStoredZoom());
  const pendingZoomRef = useRef<number | null>(null);

  // Apply zoom to newly added DOM nodes
  const applyZoomToNewNodes = useCallback((mutations: MutationRecord[]) => {
    const offset = zoomRef.current;
    if (offset === 0) return;

    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType !== Node.ELEMENT_NODE) return;
        const el = node as HTMLElement;
        const elements = [el, ...Array.from(el.querySelectorAll('*'))];
        elements.forEach((htmlEl) => {
          if (
            htmlEl.tagName === 'SCRIPT' ||
            htmlEl.tagName === 'STYLE' ||
            htmlEl.tagName === 'META' ||
            htmlEl.tagName === 'LINK' ||
            isIconElement(htmlEl)
          )
            return;
          if (htmlEl.getAttribute(DATA_ATTR)) return;

          const computed = window.getComputedStyle(
            htmlEl as HTMLElement
          ).fontSize;
          const originalSize = parseFloat(computed);
          if (isNaN(originalSize) || originalSize >= FONT_SIZE_THRESHOLD)
            return;

          // The computed size might already include a previous offset if inherited,
          // so we need the raw stylesheet value. Use the computed value minus current offset
          // if the element inherits from an already-adjusted parent.
          const parentAttr = (
            htmlEl as HTMLElement
          ).parentElement?.getAttribute(DATA_ATTR);
          let baseSize: number;
          if (parentAttr) {
            // If parent was adjusted, computed value already reflects parent's adjustment.
            // This element's own font-size is relative to that, so we take computed as-is
            // and subtract the offset to get original.
            baseSize = originalSize - offset;
          } else {
            baseSize = originalSize;
          }

          if (baseSize >= FONT_SIZE_THRESHOLD) return;

          (htmlEl as HTMLElement).setAttribute(DATA_ATTR, String(baseSize));
          const newSize = baseSize + offset;
          (htmlEl as HTMLElement).style.fontSize = `${Math.max(newSize, 8)}px`;
        });
      });
    });
  }, []);

  useEffect(() => {
    // If this zoom level was triggered by a button click, pendingZoomRef is set
    // and isZooming is already true (set synchronously in the click handler).
    // We just need to do the DOM work and then clear the spinner.
    zoomRef.current = zoomLevel;
    pendingZoomRef.current = zoomLevel;

    // Double-RAF: first frame lets React flush the isZooming=true render to screen,
    // second frame is when the browser has actually painted the spinner.
    const rafId = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (pendingZoomRef.current === zoomLevel) {
          applyZoomToDOM(zoomLevel);
          setIsZooming(false);
          pendingZoomRef.current = null;

          if (observerRef.current) {
            observerRef.current.disconnect();
          }
          const observer = new MutationObserver(applyZoomToNewNodes);
          observer.observe(document.body, { childList: true, subtree: true });
          observerRef.current = observer;
        }
      });
    });

    return () => {
      cancelAnimationFrame(rafId);
      observerRef.current?.disconnect();
    };
  }, [zoomLevel, applyZoomToNewNodes]);

  const zoomIn = useCallback(() => {
    setIsZooming(true);
    setZoomLevel((prev) => {
      const next = Math.min(prev + 1, MAX_ZOOM);
      localStorage.setItem(LOCALE_STORAGE_KEYS.ZOOM_LEVEL, String(next));
      return next;
    });
  }, []);

  const zoomOut = useCallback(() => {
    setIsZooming(true);
    setZoomLevel((prev) => {
      const next = Math.max(prev - 1, MIN_ZOOM);
      localStorage.setItem(LOCALE_STORAGE_KEYS.ZOOM_LEVEL, String(next));
      return next;
    });
  }, []);

  const resetZoom = useCallback(() => {
    setIsZooming(true);
    setZoomLevel(0);
    localStorage.setItem(LOCALE_STORAGE_KEYS.ZOOM_LEVEL, '0');
  }, []);

  return {
    zoomLevel,
    zoomIn,
    zoomOut,
    resetZoom,
    isZooming,
    MIN_ZOOM,
    MAX_ZOOM,
  };
};
